/** Exceptions tab - this supplier's open/in-progress governance exceptions, filtered from
 * the shared exceptions seed list (see `supplierProfileData.ts`). Actions are local state
 * only (Demo Mode, no backend persistence). */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { Section } from '../../../../../components/ui/Section';
import { getSupplierExceptions, SupplierExceptionItem } from '../../../../../mocks/data/supplierProfileData';
import {
  EXCEPTION_CATEGORY_LABEL, EXCEPTION_SEVERITY_COLOR, EXCEPTION_STATUS_COLOR,
} from '../../components/supplierExceptionChips';

export interface ExceptionsTabProps {
  supplierId: number;
}

const ExceptionsTab: React.FC<ExceptionsTabProps> = ({ supplierId }) => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [exceptions, setExceptions] = useState<SupplierExceptionItem[]>(() => getSupplierExceptions(supplierId));

  useEffect(() => {
    setExceptions(getSupplierExceptions(supplierId));
  }, [supplierId]);

  const resolve = (id: number, status: SupplierExceptionItem['status']) => {
    setExceptions((current) => current.map((exception) => (exception.id === id ? { ...exception, status } : exception)));
  };

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Stack direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}>
          <Typography variant="textSM"
            fontWeight={600}>{t('supplierExceptions', 'Exceptions')}</Typography>
          <Button variant="secondary"
            size="md"
            onClick={() => navigate('/procurement/suppliers/exceptions')}>
            {t('viewAllExceptions', 'View all supplier exceptions')}
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('exception', 'Exception')}</TableCell>
              <TableCell>{t('category', 'Category')}</TableCell>
              <TableCell>{t('severity', 'Severity')}</TableCell>
              <TableCell>{t('owner', 'Owner')}</TableCell>
              <TableCell>{t('dueDate', 'Due date')}</TableCell>
              <TableCell>{t('status', 'Status')}</TableCell>
              <TableCell align="right">{t('actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exceptions.length === 0 && (
              <TableRow><TableCell colSpan={7}>{t('noExceptions', 'No open exceptions for this supplier.')}</TableCell></TableRow>
            )}
            {exceptions.map((exception) => (
              <TableRow key={exception.id}>
                <TableCell>{exception.exception}</TableCell>
                <TableCell>{EXCEPTION_CATEGORY_LABEL[exception.category] ?? exception.category}</TableCell>
                <TableCell><Chip label={exception.severity}
                  size="small"
                  color={EXCEPTION_SEVERITY_COLOR[exception.severity]} /></TableCell>
                <TableCell>{exception.owner ?? '-'}</TableCell>
                <TableCell>{exception.dueDate ?? '-'}</TableCell>
                <TableCell><Chip label={exception.status}
                  size="small"
                  color={EXCEPTION_STATUS_COLOR[exception.status]} /></TableCell>
                <TableCell align="right">
                  <Stack direction="row"
                    spacing={1}
                    justifyContent="flex-end">
                    {exception.status !== 'Resolved' && (
                      <Button size="md"
                        variant="primary"
                        onClick={() => resolve(exception.id, 'Resolved')}>{t('resolve', 'Resolve')}</Button>
                    )}
                    {exception.status !== 'Waived' && exception.status !== 'Resolved' && (
                      <Button size="md"
                        variant="secondary"
                        onClick={() => resolve(exception.id, 'Waived')}>{t('waive', 'Waive')}</Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
};

export default ExceptionsTab;
